import React, { useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import { Warning, X } from '@phosphor-icons/react'
import {
  useGetIntegrationStatusQuery,
  useUpdateIntegrationStatusMutation,
} from '../../redux/timeoff.endpoints'
import bamboohrLogo from '../../assets/bamboohr_logo.png'
import { toast } from 'react-toastify'

type ModalBambooIntegrationProps = {
  isOpen: boolean
  onClose: () => void
}

type IntegrationStatusProps = {
  isOn: boolean
  setIsOn: (value: boolean) => void
}

const WarningMessage = () => {
  return (
    <div className="bamboo-modal__warning">
      <Warning
        size={24}
        weight="fill"
        color="#E9C46A"
        className="bamboo-modal__warning-icon"
        aria-hidden
      />
      <p className="bamboo-modal__warning-text">
        Before turning off any integration setting, understand its current
        functionality and the potential impact of disabling it. Consider how it
        may affect data flow, processes, and users
      </p>
    </div>
  )
}

const IntegrationStatus = ({ isOn, setIsOn }: IntegrationStatusProps) => {
  return (
    <div
      className="bamboo-modal__options"
      role="radiogroup"
      aria-label="Bamboo integration status"
    >
      <div
        className="bamboo-modal__option"
        role="radio"
        aria-checked={isOn}
        tabIndex={0}
        onClick={() => setIsOn(true)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOn(true)
          }
        }}
      >
        <div
          className={`bamboo-modal__radio${
            isOn ? ' bamboo-modal__radio--checked' : ''
          }`}
          aria-hidden
        >
          {isOn ? <div className="bamboo-modal__radio-dot" /> : null}
        </div>
        <div>
          <p className="bamboo-modal__option-title">Bamboo integration is ON</p>
          <p className="bamboo-modal__option-desc">
            Bamboo Time Off integrations are on. All team information will
            automatically sync.
          </p>
        </div>
      </div>

      <div
        className="bamboo-modal__option"
        role="radio"
        aria-checked={!isOn}
        tabIndex={0}
        onClick={() => setIsOn(false)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setIsOn(false)
          }
        }}
      >
        <div
          className={`bamboo-modal__radio${
            !isOn ? ' bamboo-modal__radio--checked' : ''
          }`}
          aria-hidden
        >
          {!isOn ? <div className="bamboo-modal__radio-dot" /> : null}
        </div>
        <div>
          <p className="bamboo-modal__option-title">
            Bamboo integration is OFF
          </p>
          <p className="bamboo-modal__option-desc">
            Bamboo Time Off integrations are off. All team information will no
            longer be synced, information can be lost.
          </p>
        </div>
      </div>
    </div>
  )
}

export default function ModalBambooIntegration({
  isOpen,
  onClose,
}: ModalBambooIntegrationProps) {
  const [isOn, setIsOn] = React.useState(true)
  const { data, isSuccess } = useGetIntegrationStatusQuery('bamboohr')
  const [updateStatus] = useUpdateIntegrationStatusMutation()

  useEffect(() => {
    if (data && isSuccess) {
      setIsOn(data.isActive)
    }
  }, [data, isSuccess])

  const handleSave = async () => {
    await updateStatus({ integrationName: 'bamboohr', isActive: isOn })
    toast.success('Integration status updated successfully', {
      toastId: 'integration-status-updated',
    })
    onClose()
  }

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      dialogClassName="bamboo-modal-dialog"
      contentClassName="bamboo-modal-content"
    >
      <div className="bamboo-modal">
        <button
          type="button"
          className="bamboo-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={20} weight="regular" />
        </button>

        <div className="bamboo-modal__header">
          <div className="bamboo-modal__logo-wrap">
            <img
              src={bamboohrLogo}
              alt="BambooHR"
              className="bamboo-modal__logo"
            />
          </div>
          <h2 className="bamboo-modal__title">Integration Settings</h2>
        </div>

        <WarningMessage />
        <IntegrationStatus isOn={isOn} setIsOn={setIsOn} />

        <div className="bamboo-modal__footer">
          <button
            type="button"
            className="bamboo-modal__btn bamboo-modal__btn--cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="bamboo-modal__btn bamboo-modal__btn--save"
            onClick={handleSave}
          >
            Save
          </button>
        </div>
      </div>
    </Modal>
  )
}
